import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ComicCard } from '../components/ComicCard';
import { StoryCard } from '../components/StoryCard';
import { useExploreComics, useFeaturedComics, useExploreStories, useLanguages } from '../hooks/useComics';
import { useUILocale } from '../hooks/useUILocale';
import { EXPLORE_LANG_STORAGE_KEY, isIso2 } from '../locale';
import type { ExploreComicsParams, ExploreStoriesParams } from '../services/api';

const ISO2 = /^[a-z]{2}$/;

type Tab = 'stories' | 'comics';

const SORT_OPTIONS: { value: 'popular' | 'recent'; label: string }[] = [
  { value: 'popular', label: 'Popular' },
  { value: 'recent', label: 'Recent' },
];

const PERIOD_OPTIONS: { value: 'day' | 'week' | 'month' | 'all'; label: string }[] = [
  { value: 'day', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'all', label: 'All time' },
];

const THEME_OPTIONS = ['all', 'fantasy', 'detective', 'horror', 'romance', 'scifi', 'comedy', 'intimate'];

function browserLanguage(): string {
  return (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en')
    .toLowerCase()
    .slice(0, 2);
}

function storedContentLanguage(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(EXPLORE_LANG_STORAGE_KEY);
    return v && ISO2.test(v) ? v : null;
  } catch {
    return null;
  }
}

function persistContentLanguage(code: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(EXPLORE_LANG_STORAGE_KEY, code);
  } catch {
    // ignore storage errors
  }
}

/**
 * Manage <link rel="canonical"> and hreflang alternates for the current
 * Explore variant. Canonical reflects the content filter; hreflang alternates
 * cover every UI locale × current content-lang combination (currently only
 * English UI, ready to expand when translations land).
 */
function useExploreHeadTags(uiLocale: string, contentLang: string, supportedUILocales: readonly string[]) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const origin = window.location.origin;
    const set = (rel: string, hreflang: string | null, href: string) => {
      const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
      let el = document.head.querySelector<HTMLLinkElement>(sel);
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        if (hreflang) el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.href = href;
    };
    const remove = (sel: string) => {
      document.head.querySelectorAll(sel).forEach((el) => el.remove());
    };

    // Canonical = current UI × content combination
    set('canonical', null, `${origin}/${uiLocale}/explore/${contentLang}`);

    // hreflang covers each supported UI locale variant of the same content filter
    remove('link[rel="alternate"][hreflang]');
    for (const ui of supportedUILocales) {
      set('alternate', ui, `${origin}/${ui}/explore/${contentLang}`);
    }
    set('alternate', 'x-default', `${origin}/explore/${contentLang}`);

    return () => {
      remove('link[rel="alternate"][hreflang]');
      remove('link[rel="canonical"]');
    };
  }, [uiLocale, contentLang, supportedUILocales.join(',')]);
}

function labelForLanguage(code: string): string {
  try {
    // Render each language in its own native name (e.g. "Deutsch" for de)
    const dn = new Intl.DisplayNames([code], { type: 'language' });
    const name = dn.of(code);
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

const ExplorePage: React.FC = () => {
  const uiLocale = useUILocale();
  const { contentLang: urlContentLang } = useParams<{ contentLang?: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('stories');
  const langQuery = useLanguages();
  const languages = langQuery.data?.languages || [];

  // Validate URL content-lang (only accept ISO 639-1 lowercase codes)
  const urlContentValid = isIso2(urlContentLang) ? urlContentLang : null;

  // Preferred content language: URL > storage > browser > top-count > 'en'
  const preferredContent = useMemo(() => {
    if (urlContentValid) return urlContentValid;
    const stored = storedContentLanguage();
    if (stored) return stored;
    const browser = browserLanguage();
    if (languages.some((l) => l.code === browser)) return browser;
    if (languages.length > 0) return languages[0].code;
    return browser || 'en';
  }, [urlContentValid, languages]);

  // Redirect to a canonical URL with both UI locale and content lang
  useEffect(() => {
    if (!urlContentValid && preferredContent) {
      navigate(`/${uiLocale}/explore/${preferredContent}`, { replace: true });
    }
  }, [urlContentValid, preferredContent, uiLocale, navigate]);

  const effectiveContent = urlContentValid || preferredContent;

  useExploreHeadTags(uiLocale, effectiveContent, ['en']);

  // Persist user's content-lang pick
  useEffect(() => {
    if (urlContentValid) persistContentLanguage(urlContentValid);
  }, [urlContentValid]);

  const onLanguageChange = (next: string) => {
    if (!ISO2.test(next)) return;
    navigate(`/${uiLocale}/explore/${next}`, { replace: true });
  };

  // Until we have a valid content lang in URL, render nothing — the redirect
  // above lands us back here with one.
  if (!urlContentValid) return null;

  // Ensure the selector always includes the current content language even if
  // the server's aggregate doesn't list it yet (e.g. first ever Italian user).
  const selectorLangs = languages.some((l) => l.code === effectiveContent)
    ? languages
    : [{ code: effectiveContent, count: 0 }, ...languages];

  return (
    <Layout wide>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Explore</h1>
            <p className="text-gray-500">Stories and comics created by the StoryChain community.</p>
          </div>
          {selectorLangs.length > 0 && (
            <select
              value={effectiveContent}
              onChange={(e) => onLanguageChange(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
              aria-label="Language"
            >
              {selectorLangs.map((l) => (
                <option key={l.code} value={l.code}>
                  {labelForLanguage(l.code)}{l.count > 0 ? ` · ${l.count}` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="border-b border-gray-200">
          <div className="flex gap-1">
            {(['stories', 'comics'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition capitalize ${
                  tab === t
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {tab === 'stories' ? <StoriesSection language={effectiveContent} /> : <ComicsSection language={effectiveContent} />}
      </div>
    </Layout>
  );
};

const StoriesSection: React.FC<{ language: string }> = ({ language }) => {
  const [sort, setSort] = useState<'popular' | 'recent'>('popular');
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'all'>('week');
  const [page, setPage] = useState(1);

  const params: ExploreStoriesParams = { sort, period, page, limit: 20, language };
  const q = useExploreStories(params);

  const stories = q.data?.stories || [];
  const total = q.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / (params.limit || 20)));

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value as 'popular' | 'recent'); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={period}
          onChange={(e) => { setPeriod(e.target.value as 'day' | 'week' | 'month' | 'all'); setPage(1); }}
          className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
        >
          {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {q.isLoading ? (
        <GridSkeleton />
      ) : stories.length === 0 ? (
        <EmptyState message="No stories match these filters yet." />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {stories.map((s) => <StoryCard key={s.id} story={s} />)}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </section>
  );
};

const ComicsSection: React.FC<{ language: string }> = ({ language }) => {
  const [sort, setSort] = useState<'popular' | 'recent'>('popular');
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'all'>('week');
  const [theme, setTheme] = useState<string>('all');
  const [page, setPage] = useState(1);

  const filters: ExploreComicsParams = {
    sort, period, page, limit: 20, language,
    ...(theme !== 'all' ? { theme } : {}),
  };

  const featured = useFeaturedComics();
  const explore = useExploreComics(filters);

  const featuredComics = featured.data?.comics || [];
  const exploreComics = explore.data?.comics || [];
  const total = explore.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / (filters.limit || 20)));

  return (
    <>
      {featured.isLoading ? (
        <div className="h-48 bg-white rounded-xl animate-pulse" />
      ) : featuredComics.length > 0 ? (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Featured</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {featuredComics.map((c) => <ComicCard key={c.id} comic={c} />)}
          </div>
        </section>
      ) : null}

      <section>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value as 'popular' | 'recent'); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={period}
            onChange={(e) => { setPeriod(e.target.value as 'day' | 'week' | 'month' | 'all'); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
            disabled={sort === 'recent'}
          >
            {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            value={theme}
            onChange={(e) => { setTheme(e.target.value); setPage(1); }}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm capitalize"
          >
            {THEME_OPTIONS.map((t) => <option key={t} value={t}>{t === 'all' ? 'All themes' : t}</option>)}
          </select>
        </div>

        {explore.isLoading ? (
          <GridSkeleton />
        ) : exploreComics.length === 0 ? (
          <EmptyState message="No comics match these filters yet." />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {exploreComics.map((c) => <ComicCard key={c.id} comic={c} />)}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>
    </>
  );
};

const GridSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
    {Array.from({ length: 10 }).map((_, i) => (
      <div key={i} className="aspect-[3/4] bg-white rounded-xl animate-pulse" />
    ))}
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-16 bg-white rounded-xl">
    <p className="text-gray-500">{message}</p>
  </div>
);

const Pagination: React.FC<{ page: number; totalPages: number; onChange: (p: number) => void }> = ({
  page, totalPages, onChange,
}) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 mt-8">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="px-4 py-2 rounded-lg bg-white border border-gray-200 disabled:opacity-40 text-sm"
      >
        Previous
      </button>
      <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className="px-4 py-2 rounded-lg bg-white border border-gray-200 disabled:opacity-40 text-sm"
      >
        Next
      </button>
    </div>
  );
};

export default ExplorePage;
